import { useState, useEffect, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { SkeletonBlock, SkeletonText } from "../Skeleton/Skeleton";

// Shorter timeout for faster fallback
const LOAD_TIMEOUT_MS = 3000;

// List of domains known to block iframe embedding - these open immediately in new tab
const IFRAME_BLOCKED_DOMAINS = [
  "youtube.com",
  "youtu.be",
  "m.youtube.com",
  "twitter.com",
  "x.com",
  "facebook.com",
  "m.facebook.com",
  "instagram.com",
  "linkedin.com",
  "tiktok.com",
  "reddit.com",
  "github.com",
  "google.com",
  "docs.google.com",
  "drive.google.com",
  "amazon.com",
  "netflix.com",
  "spotify.com",
  "apple.com",
  "microsoft.com",
  "whatsapp.com",
  "telegram.org",
  "discord.com",
  "twitch.tv",
  "vimeo.com",
  "pinterest.com",
  "snapchat.com",
  "quora.com",
  "medium.com",
  "substack.com",
  "cnbc.com",
  "cnn.com",
  "bbc.com",
  "nytimes.com",
  "washingtonpost.com",
  "forbes.com",
  "bloomberg.com",
  "reuters.com",
  "wsj.com",
  "theguardian.com",
  "huffpost.com",
  "businessinsider.com",
  "techcrunch.com",
  "wired.com",
  "mashable.com",
  "engadget.com",
  "arstechnica.com",
  "theverge.com",
  "cnet.com",
  "zdnet.com",
];

// Check if a URL's domain is in the known blocked list
const isKnownBlockedDomain = (url) => {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return IFRAME_BLOCKED_DOMAINS.some(
      (domain) => hostname === domain || hostname.endsWith("." + domain)
    );
  } catch {
    return false;
  }
};

const MobileLinkViewer = ({ url, displaySite, onClose }) => {
  const [status, setStatus] = useState("loading"); // "loading" | "loaded" | "blocked"
  const iframeRef = useRef(null);
  const timeoutRef = useRef(null);
  const hasRedirected = useRef(false);

  const openInBrowserAndClose = useCallback(() => {
    // Prevent multiple redirects
    if (hasRedirected.current) return;
    hasRedirected.current = true;

    clearTimeout(timeoutRef.current);
    window.open(url, "_blank", "noopener,noreferrer");
    onClose();
  }, [url, onClose]);

  const handleClose = useCallback(() => {
    clearTimeout(timeoutRef.current);
    onClose();
  }, [onClose]);

  const handleOpenInBrowser = useCallback(() => {
    clearTimeout(timeoutRef.current);
    window.open(url, "_blank", "noopener,noreferrer");
    onClose();
  }, [url, onClose]);

  // Check if URL is in known blocklist - redirect immediately
  useEffect(() => {
    if (isKnownBlockedDomain(url)) {
      openInBrowserAndClose();
    }
  }, [url, openInBrowserAndClose]);

  // Listen for CSP violation errors (catches frame-ancestors blocks)
  useEffect(() => {
    if (isKnownBlockedDomain(url)) return;

    const handleSecurityPolicyViolation = (e) => {
      // Check if the violation is related to frame-ancestors (iframe blocking)
      if (e.violatedDirective?.includes("frame-ancestors") ||
          e.effectiveDirective?.includes("frame-ancestors")) {
        openInBrowserAndClose();
      }
    };

    document.addEventListener("securitypolicyviolation", handleSecurityPolicyViolation);
    return () => {
      document.removeEventListener("securitypolicyviolation", handleSecurityPolicyViolation);
    };
  }, [url, openInBrowserAndClose]);

  // Body scroll lock + back button support
  useEffect(() => {
    if (isKnownBlockedDomain(url)) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.history.pushState({ mobileLinkViewer: true }, "");
    const handlePopState = () => handleClose();
    window.addEventListener("popstate", handlePopState);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("popstate", handlePopState);
    };
  }, [handleClose, url]);

  // Timeout fallback — if iframe hasn't loaded successfully, open in browser
  useEffect(() => {
    if (isKnownBlockedDomain(url)) return;

    timeoutRef.current = setTimeout(() => {
      if (status === "loading") {
        openInBrowserAndClose();
      }
    }, LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeoutRef.current);
  }, [status, openInBrowserAndClose, url]);

  const handleIframeLoad = () => {
    clearTimeout(timeoutRef.current);

    if (hasRedirected.current) return;

    const iframe = iframeRef.current;
    if (!iframe) return;

    // Multiple checks to detect if iframe content was blocked:
    try {
      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;

      // Check 1: If we can access contentDocument, it means the page didn't load
      // (blocked pages stay at about:blank which is same-origin)
      if (doc) {
        // Check if it's about:blank or empty
        const bodyContent = doc.body?.innerHTML || "";
        const docUrl = doc.URL || "";

        if (docUrl === "about:blank" ||
            bodyContent === "" ||
            bodyContent.trim().length === 0) {
          openInBrowserAndClose();
          return;
        }

        // If we can access the document and it has content, still blocked
        // because cross-origin loaded pages throw SecurityError
        openInBrowserAndClose();
        return;
      }

      // Check 2: Try to access contentWindow.location
      // This will throw for successfully loaded cross-origin pages
      if (win) {
        try {
          // This should throw SecurityError for cross-origin pages
          const loc = win.location.href;
          // If we can read it and it's about:blank, it's blocked
          if (loc === "about:blank") {
            openInBrowserAndClose();
            return;
          }
        } catch (e) {
          // SecurityError = cross-origin page loaded successfully
          setStatus("loaded");
          return;
        }
      }
    } catch (e) {
      // SecurityError when accessing contentDocument = successfully loaded cross-origin page
      if (e.name === "SecurityError") {
        setStatus("loaded");
        return;
      }
    }

    // Default: assume loaded if we get here
    setStatus("loaded");
  };

  const handleIframeError = () => {
    clearTimeout(timeoutRef.current);
    openInBrowserAndClose();
  };

  // Don't render if URL is in known blocklist - redirects immediately
  if (isKnownBlockedDomain(url)) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[1001] flex flex-col">
      {/* Toolbar */}
      <div className="bg-white flex items-center px-3 py-2 shadow-md min-h-[48px]">
        <button
          onClick={handleClose}
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100"
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} className="text-lg text-gray-700" />
        </button>

        <span className="flex-1 text-center text-sm text-gray-500 truncate px-2">
          {displaySite}
        </span>

        <button
          onClick={handleOpenInBrowser}
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100"
          aria-label="Open in browser"
        >
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-sm text-gray-700" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 relative bg-white overflow-hidden">
        {/* Page-shaped skeleton while the embedded page loads. */}
        {status === "loading" && (
          <div
            className="absolute inset-0 bg-white p-4"
            aria-busy="true"
            aria-label="Page loading"
          >
            <SkeletonBlock height={200} rounded="rounded-lg" />
            <SkeletonBlock height={18} width="72%" className="mt-5" />
            <SkeletonText lines={4} height={12} lastLineWidth="45%" className="mt-4" />
          </div>
        )}

        {/* Iframe */}
        {status !== "error" && (
          <iframe
            ref={iframeRef}
            src={url}
            title="External content"
            className="w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        )}
      </div>
    </div>
  );
};

export default MobileLinkViewer;
